/**
 * Orchestrate a Local MCP delete: call the service and apply the outcome via callbacks.
 * The caller supplies state setters and toast/dialog callbacks; this helper decides
 * which to invoke based on the normalized service result. It performs NO client-side
 * cascade — server state is reflected only through the supplied list mutations/reload.
 *
 * @param {object}   params
 * @param {object}   params.target            - Local MCP item to delete (must have id)
 * @param {'list'|'form'} params.source       - originating entry point
 * @param {string}   params.corporationInuse
 * @param {string}   params.userId
 * @param {function} params.deleteService     - deleteMcpCatalog (injected for testability)
 * @param {function} params.removeItem        - (id) => void, removes item from Catalog_List
 * @param {function} params.onSuccessToast    - () => void
 * @param {function} params.onErrorToast      - () => void  (COMMSG001)
 * @param {function} params.onDataChanged     - () => Promise<void>, opens COMMSG030 + reloads
 * @param {function} params.onCloseForm       - () => void
 * @returns {Promise<'ok'|'dataChanged'|'systemError'>} the applied outcome
 */
export const runMcpCatalogDelete = async ({
    target,
    source,
    corporationInuse,
    userId,
    deleteService,
    removeItem,
    onSuccessToast,
    onErrorToast,
    onDataChanged,
    onCloseForm
}) => {
    const result = await deleteService(target.id, corporationInuse, userId);

    if (result.ok) {
        removeItem(target.id);
        onSuccessToast();
        if (source === 'form') {
            onCloseForm();
        }
        return 'ok';
    }

    if (result.dataChanged) {
        if (source === 'form') {
            onCloseForm();
        }
        await onDataChanged();
        return 'dataChanged';
    }

    // systemError — keep item, keep form open, show error toast
    onErrorToast();
    return 'systemError';
};
